import { groupBy } from 'lodash';
import i18next from 'i18next';

import utilsXLS, { EXPORT_DECIMALS_COUNT } from '@commons/utils/makeXLS';

import {
  ALLERGENS_COLUMN,
  BRANDS_COLUMN,
  CHANNEL_COMPONENT_COST_COLUMN,
  CHANNEL_COMPONENT_USED_COLUMN,
  CHANNEL_INGREDIENT_COST_COLUMN,
  CHANNEL_INGREDIENT_QUANTITY_COLUMN,
  CHANNEL_MARGIN_COLUMN,
  CHANNEL_RECIPE_COST_COLUMN,
  COMMON_COLUMNS,
  COMPONENT_ALLERGENS_COLUMN,
  COMPONENT_CATEGORY_COLUMN,
  COMPONENT_COMPONENTS_ID_COLUMN,
  COMPONENT_COST_COLUMN,
  COMPONENT_NAME_COLUMN,
  COMPONENT_QUANTITY_COLUMN,
  COMPONENT_TYPE_COLUMN,
  COMPONENT_UNIT_COLUMN,
  DEFAULT_COST_COLUMN,
  INGREDIENT_ALLERGENS_COLUMN,
  INGREDIENT_CATEGORY_COLUMN,
  INGREDIENT_COST_COLUMN,
  INGREDIENT_ID_COLUMN,
  INGREDIENT_NAME_COLUMN,
  INGREDIENT_QUANTITY_COLUMN,
  INGREDIENT_UNIT_COLUMN,
  RECIPE_DEFAULT_COST_COLUMN,
  AVAILABILITIES_COLUMNS,
} from './exportRecipesColumns';
import { VARIANT_COLUMN } from './exportRecipesByStoreColumns';

const applyDecimalsOnNumericColumns = (columns) =>
  columns.map((column) => {
    const isNumericColumn = column.type === 'currency' || column.type === 'percentage';

    if (!isNumericColumn) {
      return column;
    }

    return {
      ...column,
      decimals: EXPORT_DECIMALS_COUNT,
    };
  });

const aggregateIngredients = (ingredients, clientChannels) => {
  const hasMultipleChannels = clientChannels.length > 1;

  // variantName is part of the key so a recipe's variants are not merged together (it is undefined
  // when the variant column is off, which keeps the original grouping)
  const groupedIngredientsByRecipeId = groupBy(
    ingredients,
    ({ componentId, recipeId, variantName }) => `${componentId}-${recipeId}-${variantName}`,
  );

  const emptyChannelMap = (defaultValue) =>
    Object.fromEntries(clientChannels.map((channel) => [channel.id, defaultValue]));

  const aggregatedIngredients = Object.values(groupedIngredientsByRecipeId).map(
    (ingredientGroup) => {
      if (ingredientGroup.length === 1 || !hasMultipleChannels) {
        return ingredientGroup[0];
      }

      // Aggregate per-channel data across duplicates of the same ingredient
      const aggregated = ingredientGroup.reduce(
        (acc, ingredient) => {
          clientChannels.forEach((channel) => {
            if (!ingredient.componentIsUsedByChannelId[channel.id]) {
              return;
            }

            acc.componentIsUsedByChannelId[channel.id] = true;

            acc.componentCostByChannelId[channel.id] +=
              ingredient.componentCostByChannelId[channel.id] || 0;

            acc.componentQuantityByChannelId[channel.id] += ingredient.componentQuantity;
          });

          return acc;
        },
        {
          componentIsUsedByChannelId: emptyChannelMap(false),
          componentCostByChannelId: emptyChannelMap(0),
          componentQuantityByChannelId: emptyChannelMap(0),
        },
      );

      return { ...ingredientGroup[0], ...aggregated };
    },
  );

  const formattedIngredients = aggregatedIngredients.reduce((acc, ingredient) => {
    if (!ingredient.isIngredient) {
      return acc;
    }

    if (hasMultipleChannels) {
      const ingredientQuantityByChannelId = Object.fromEntries(
        clientChannels.map((channel) => [
          channel.id,
          ingredient.componentIsUsedByChannelId[channel.id]
            ? ingredient.componentQuantityByChannelId?.[channel.id] || ingredient.componentQuantity
            : 0,
        ]),
      );

      const ingredientCostByChannelId = Object.fromEntries(
        clientChannels.map((channel) => [
          channel.id,
          ingredient.componentIsUsedByChannelId[channel.id]
            ? ingredient.componentCostByChannelId[channel.id]
            : 0,
        ]),
      );

      acc.push({ ...ingredient, ingredientQuantityByChannelId, ingredientCostByChannelId });
    } else {
      acc.push(ingredient);
    }

    return acc;
  }, []);

  return formattedIngredients;
};

// BRANDS is placed right after STATUS, shared across all sheets. VARIANT, when enabled, is col C
// (right after NAME) on every sheet.
const getRecipeCommonColumns = (isMainSheet, clientHasMultipleBrands, hasVariantColumn) => [
  COMMON_COLUMNS.ID(isMainSheet),
  COMMON_COLUMNS.NAME(),
  ...(hasVariantColumn ? [VARIANT_COLUMN()] : []),
  COMMON_COLUMNS.STATUS(),
  ...(clientHasMultipleBrands ? [BRANDS_COLUMN()] : []),
  COMMON_COLUMNS.CATEGORY(isMainSheet),
  COMMON_COLUMNS.ASSOCIATED_PRODUCT(),
  COMMON_COLUMNS.QUANTITY(isMainSheet),
  COMMON_COLUMNS.UNIT(isMainSheet),
];

const getMainSheetColumns = (clientChannels, clientHasMultipleBrands, hasVariantColumn) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const channelMarginColumns = clientChannels.map((channel) => CHANNEL_MARGIN_COLUMN(channel));
  const channelCostColumns = clientChannels.map((channel) => CHANNEL_RECIPE_COST_COLUMN(channel));

  const costAndMarginColumns = hasMultipleChannels
    ? [...channelMarginColumns, ...channelCostColumns]
    : [DEFAULT_COST_COLUMN()];

  const formattedCostAndMarginColumns = applyDecimalsOnNumericColumns(costAndMarginColumns);

  return [
    ...getRecipeCommonColumns(true, clientHasMultipleBrands, hasVariantColumn),
    ...formattedCostAndMarginColumns,
    ALLERGENS_COLUMN(),
    ...AVAILABILITIES_COLUMNS(),
  ];
};

const getComponentsSheetColumns = (clientChannels, clientHasMultipleBrands, hasVariantColumn) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const recipeCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_RECIPE_COST_COLUMN(channel))
    : [RECIPE_DEFAULT_COST_COLUMN()];

  // All "Used (X)" columns grouped together, then all "Cost component (X)" grouped together
  const componentUsedColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_COMPONENT_USED_COLUMN(channel))
    : [];

  const componentCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_COMPONENT_COST_COLUMN(channel))
    : [COMPONENT_COST_COLUMN()];

  return [
    ...getRecipeCommonColumns(false, clientHasMultipleBrands, hasVariantColumn),
    ...recipeCostColumns,
    COMPONENT_COMPONENTS_ID_COLUMN(),
    COMPONENT_NAME_COLUMN(),
    COMPONENT_TYPE_COLUMN(),
    COMPONENT_CATEGORY_COLUMN(),
    COMPONENT_QUANTITY_COLUMN(),
    ...componentUsedColumns,
    ...componentCostColumns,
    COMPONENT_UNIT_COLUMN(),
    COMPONENT_ALLERGENS_COLUMN(),
  ];
};

const getIngredientsSheetColumns = (clientChannels, clientHasMultipleBrands, hasVariantColumn) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const recipeCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_RECIPE_COST_COLUMN(channel))
    : [RECIPE_DEFAULT_COST_COLUMN()];

  const ingredientQuantityColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_INGREDIENT_QUANTITY_COLUMN(channel))
    : [INGREDIENT_QUANTITY_COLUMN()];

  const ingredientCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_INGREDIENT_COST_COLUMN(channel))
    : [INGREDIENT_COST_COLUMN()];

  return [
    ...getRecipeCommonColumns(false, clientHasMultipleBrands, hasVariantColumn),
    ...recipeCostColumns,
    INGREDIENT_ID_COLUMN(),
    INGREDIENT_NAME_COLUMN(),
    INGREDIENT_CATEGORY_COLUMN(),
    INGREDIENT_UNIT_COLUMN(),
    ...ingredientQuantityColumns,
    INGREDIENT_ALLERGENS_COLUMN(),
    ...ingredientCostColumns,
  ];
};

/**
 * Handle the export of the analytics reference data into a XLS file
 *
 * @param {Array} detailedRecipesList - The list of recipes to export in the XLS file
 * @param {Array} detailedRecipesWithComponents - The list of recipe components to export
 * @param {String} currency - The currency symbol
 * @param {Object[]} clientChannels - The list of channels for the client
 * @param {Boolean} clientHasMultipleBrands - Whether the client has multiple brands
 * @param {Boolean} isInCentralMode - Whether we are in central kitchen mode
 * @param {Boolean} hasVariantColumn - Whether to add the "Variant" column (col C) on every sheet
 *
 * @returns {void}
 */
export function exportRecipes(
  detailedRecipesList,
  detailedRecipesWithComponents,
  currency,
  clientChannels,
  clientHasMultipleBrands,
  isInCentralMode = false,
  hasVariantColumn = false,
) {
  if (!detailedRecipesList) {
    return;
  }

  const mainSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.RECIPE_PLURAL'),
    getMainSheetColumns(clientChannels, clientHasMultipleBrands, hasVariantColumn),
    detailedRecipesList,
    currency,
  );

  const componentsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('ADMIN.RECIPES.COMPONENTS'),
    getComponentsSheetColumns(clientChannels, clientHasMultipleBrands, hasVariantColumn),
    detailedRecipesWithComponents.filter(({ isPreparationIngredient }) => !isPreparationIngredient),
    currency,
  );

  const detailedRecipesWithIngredients = aggregateIngredients(
    detailedRecipesWithComponents,
    clientChannels,
  );

  const ingredientsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INGREDIENT_PLURAL'),
    getIngredientsSheetColumns(clientChannels, clientHasMultipleBrands, hasVariantColumn),
    detailedRecipesWithIngredients,
    currency,
  );

  utilsXLS.makeXLS(
    i18next.t(
      isInCentralMode
        ? 'ADMIN.RECIPES.EXPORT_FILENAME_CENTRAL_KITCHEN'
        : 'ADMIN.RECIPES.EXPORT_FILENAME',
    ),
    [mainSheet, componentsSheet, ingredientsSheet],
  );
}
